import { observer } from 'mobx-react-lite';
import { useRef, useState, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Hotkey } from '@/components/blocks/Hotkey';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Rail } from '@/design-system-v2/rail';
import type { ShortcutId } from '@/features/shortcuts';
import { cn } from '@/lib/utils';

import { useActiveRecording } from './quick-create.hooks';
import {
  canRecordMeetings,
  isMeetingNoteStartBlocked,
  openMeetingNote,
  startMeetingNote,
  startNewChat,
  stopMeetingNote,
} from './quick-create.utils';

const HoverShortcut = ({ id }: { id: ShortcutId }) => (
  <span className="shrink-0 opacity-0 transition-opacity duration-150 group-data-[highlighted]/row:opacity-100 motion-reduce:transition-none">
    <Hotkey hotkey={id} size="xs" />
  </span>
);

type RowProps = {
  icon: ReactNode;
  title: string;
  meta?: ReactNode;
  hotkey?: ShortcutId;
  disabled?: boolean;
  className?: string;
  onSelect: () => void;
};

const Row = ({
  icon,
  title,
  meta,
  hotkey,
  disabled,
  className,
  onSelect,
}: RowProps) => (
  <DropdownMenu.Item
    className={cn('group/row gap-3 rounded-lg px-2.5 py-2.5', className)}
    disabled={disabled}
    onSelect={onSelect}
  >
    <span className="flex size-5 shrink-0 items-center justify-center text-foreground-subtle group-data-[highlighted]/row:text-foreground">
      {icon}
    </span>
    <span className="min-w-0 flex-1 truncate">{title}</span>
    {meta && (
      <span className="shrink-0 text-xs tabular-nums text-foreground-muted">
        {meta}
      </span>
    )}
    {hotkey && <HoverShortcut id={hotkey} />}
  </DropdownMenu.Item>
);

export const QuickCreateRail = observer(function QuickCreateRail() {
  const [open, setOpen] = useState(false);
  const closedWithPointer = useRef(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const recording = useActiveRecording();

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <Rail.Button
          ref={triggerRef}
          label="New"
          active={open}
          className="rounded-full"
        >
          <span className="flex size-8 items-center justify-center rounded-full border border-border-strong bg-secondary-active text-foreground group-hover/rail-button:bg-secondary-hover">
            <Icons.plus strokeWidth={2} className="size-4" />
          </span>
        </Rail.Button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Content
        side="right"
        align="start"
        sideOffset={10}
        alignOffset={-6}
        collisionPadding={8}
        onPointerDown={() => {
          closedWithPointer.current = true;
        }}
        onPointerDownOutside={() => {
          closedWithPointer.current = true;
        }}
        onKeyDown={() => {
          closedWithPointer.current = false;
        }}
        onCloseAutoFocus={(event) => {
          if (closedWithPointer.current) {
            event.preventDefault();
            requestAnimationFrame(() => triggerRef.current?.blur());
          }
          closedWithPointer.current = false;
        }}
        className="w-64 rounded-xl p-1.5 shadow-lg motion-opacity-in-0 motion-duration-200 motion-ease-out"
      >
        <Row
          icon={<Icons.newChat strokeWidth={1.75} className="size-4" />}
          title="New chat"
          hotkey="app.newChat"
          onSelect={startNewChat}
        />

        {recording ? (
          <div className="group/live relative">
            <Row
              icon={
                <span className="size-2 rounded-full bg-brand-cyan-cornflower-blue dark:bg-brand-jordy-blue" />
              }
              title="Meeting in progress"
              meta={
                <span className="group-hover/live:invisible group-has-[[data-highlighted]]/live:invisible">
                  {recording.elapsed}
                </span>
              }
              className="group-hover/live:bg-secondary-hover"
              onSelect={() => openMeetingNote(recording.meetingId)}
            />
            <DropdownMenu.Item
              aria-label="Stop meeting note"
              className="absolute right-1 top-1/2 size-8 w-8 -translate-y-1/2 justify-center rounded-md p-0 text-foreground-subtle opacity-0 group-hover/live:opacity-100 group-has-[[data-highlighted]]/live:opacity-100 data-[highlighted]:bg-danger-3 data-[highlighted]:text-danger-11"
              onSelect={() => void stopMeetingNote()}
            >
              <Icons.stopAlt fill="currentColor" className="!size-3" />
            </DropdownMenu.Item>
          </div>
        ) : (
          canRecordMeetings() && (
            <Row
              icon={<Icons.mic strokeWidth={1.75} className="size-[18px]" />}
              title="New meeting note"
              hotkey="meetings.toggle"
              disabled={isMeetingNoteStartBlocked()}
              onSelect={() => void startMeetingNote()}
            />
          )
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
});
